/**
 * DetailLayout — header com avatar/title + tabs + content.
 * Padrão pra páginas de detalhe (clients/:id, nerds/:id, jobs/:id, etc).
 */
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DetailLayoutProps {
  /** Voltar pra esta rota; default = histórico do navegador */
  backTo?: string;
  /** Pequena legenda acima do título (e.g. "Chamado JOB.0000123") */
  eyebrow?: ReactNode;
  /** Avatar ou ícone à esquerda do título */
  visual?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Badges/pills ao lado do título */
  badges?: ReactNode;
  /** Botões de ação à direita */
  actions?: ReactNode;
  /** Conteúdo abaixo (tabs ou content) */
  children: ReactNode;
  className?: string;
}

export function DetailLayout({
  backTo,
  eyebrow,
  visual,
  title,
  subtitle,
  badges,
  actions,
  children,
  className,
}: DetailLayoutProps) {
  const navigate = useNavigate();
  return (
    <div className={cn("space-y-6", className)}>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 text-muted-foreground"
        onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
      >
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Button>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4 min-w-0">
          {visual}
          <div className="min-w-0 space-y-1">
            {eyebrow && (
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {eyebrow}
              </p>
            )}
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
            )}
            {badges && <div className="flex flex-wrap items-center gap-1.5 pt-1">{badges}</div>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
      </header>

      <div>{children}</div>
    </div>
  );
}

/**
 * KeyValue — par chave/valor estilizado pra tabs de "Informações".
 */
export function KeyValue({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="text-sm text-foreground">{value ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

interface DetailCardProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function DetailCard({ title, description, actions, children, className }: DetailCardProps) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur overflow-hidden",
        className,
      )}
    >
      {(title || description || actions) && (
        <div className="px-6 py-4 border-b border-slate-200/60 dark:border-white/10 flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            {title && <h3 className="font-semibold text-base">{title}</h3>}
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      <div className="p-6">{children}</div>
    </div>
  );
}

export function DetailLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-primary-700 dark:text-primary-300 hover:underline font-medium">
      {children}
    </Link>
  );
}
